import React, { useContext, useEffect } from 'react';
import SeoHead from '@/components/common/seo-head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { GetStaticProps, NextLayoutComponentType } from 'next';

import MainLayout from '@/layouts/main';
import AppTitle from '@/components/common/title';
import { routes } from '@/infrastructure/const/routes';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import s from './styles.module.scss';
import { Api } from '@/api';
import { SiteContentType } from '@/infrastructure/enum/site-content.enum';
import { ISiteContent } from '@/infrastructure/interfaces/i-site-content';
import { getSiteContentMarkup } from '@/infrastructure/functions/get-site-content-markup';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface IAboutUsPage {
  content: ISiteContent;
}

const AboutUsPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IAboutUsPage>(data);
  const { content } = decrypted;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const router = useRouter();
  const { locale } = router;

  const { t: tMeta } = useTranslation('meta');
  const { t: tLinks } = useTranslation('links');
  const { t: tCommon } = useTranslation('common');

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}` + routes.aboutUs;

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('aboutUs'),
      path: routes.aboutUs,
    },
  ];

  const schema = baseMarkupSchema([
    webPageMarkupSchema(
      routes.aboutUs,
      tMeta('aboutUsPageTitle'),
      tMeta('aboutUsPageDescription')
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SeoHead
        title={tMeta('aboutUsPageTitle')}
        description={tMeta('aboutUsPageDescription')}
        canonicalURL={canonicalURL}
        schema={schema}
      />
      <main>
        <AppContainer>
          <AppTitle title={tCommon('aboutUs')} main />
          <div className={s['content']}>
            <div dangerouslySetInnerHTML={getSiteContentMarkup(content)} />
          </div>
        </AppContainer>
      </main>
    </>
  );
};

AboutUsPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale = '' } = ctx;

  const api = Api.getInstance();

  const { data: content } = await api.getSiteContentByType(
    SiteContentType.ABOUT_US
  );

  const dataToEncrypt = {
    content,
  };
  const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

  return {
    props: {
      data: encrypted,
      ...(await serverSideTranslations(locale, localeList)),
    },
  };
};

export default AboutUsPage;
